<template>
    <div class="job-gofm-report-grid" ref="reportRoot">
        <div
            v-for="block in blocks"
            :key="block.block_index || block.blockIndex"
            class="report-block mb-4"
        >
            <div v-if="block.title || block.period" class="report-block-header text-center mb-2">
                <h5 v-if="block.title" class="mb-1">{{ block.title }}</h5>
                <p v-if="block.period" class="mb-0">{{ block.period }}</p>
            </div>
            <div class="table-responsive">
                <table class="gofm-report-table">
                    <tbody>
                        <tr
                            v-for="(row, rowIndex) in block.rows"
                            :key="`${block.block_index}-${rowIndex}-${row.label}`"
                            :class="rowClass(row, rowIndex)"
                        >
                            <th class="gofm-report-label" scope="row">{{ row.label }}</th>
                            <td
                                v-for="(value, colIndex) in normalizeRowValues(row)"
                                :key="colIndex"
                                :class="[
                                    'gofm-report-value',
                                    { 'gofm-report-empty-col': !isActiveDataColumn(block, colIndex) },
                                ]"
                            >
                                {{ isActiveDataColumn(block, colIndex) ? value : '' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import jsPDF from 'jspdf';
import moment from 'moment';
import { mapGetters } from 'vuex';

const PDF_MARGIN_MM = 3;
const PDF_FOOTER_MM = 12;
const PDF_TABLE_RIGHT_EDGE_PAD_MM = 0.15;
const PDF_LOGO_PATH = '/img/logo-safe-inspection-report.jpg';
const PDF_CHECK_ICON_PATH = '/img/icon-check-black.png';
const PDF_CHECK_ICON_SIZE_MM = 2;
const PDF_DATA_COLS = 7;
const PDF_LABEL_COL_RATIO = 0.35;
const PDF_MIN_FONT_SIZE = 3.5;
const PDF_DEFAULT_FONT_SIZE = 5.5;
const PDF_HEADER_TITLE_LINE_HEIGHT = 3;
const PDF_HEADER_PERIOD_LINE_HEIGHT = 2.8;
const PDF_HEADER_TITLE_PERIOD_GAP = 0.3;
const PDF_LOGO_HEIGHT_MM = (15 * 25.4) / 72;
const PDF_FONT_FAMILY = 'Calibri';
const PDF_FONT_FILES = {
    normal: 'fonts/calibri.ttf',
    bold: 'fonts/calibrib.ttf',
};

const pdfFontCache = {
    loaded: false,
    normal: null,
    bold: null,
};

export default {
    name: 'JobGoFMReportGrid',
    props: {
        reportData: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            pdfFontFamily: PDF_FONT_FAMILY,
        };
    },
    computed: {
        ...mapGetters({
            UserLogin: 'AuthController/user',
        }),
        reportMeta() {
            if (!this.reportData) return null;
            return this.reportData.report || null;
        },
        blocks() {
            if (!this.reportData || !this.reportData.blocks) return [];
            return this.reportData.blocks;
        },
    },
    methods: {
        getPdfExportMeta() {
            const report = this.reportMeta || {};
            const userName = this.UserLogin && this.UserLogin.name ? this.UserLogin.name : '';
            return {
                exportedBy: report.printed_by || report.printedBy || userName,
                exportDate: moment().format('DD-MMM-YYYY HH:mm:ss'),
            };
        },
        rowClass(row, rowIndex) {
            if (this.isSectionRow(row)) return 'gofm-report-section-row';
            if (rowIndex === 0) return 'gofm-report-header-row';
            if (row.label === 'Block') return 'gofm-report-block-row';
            return '';
        },
        isSectionRow(row) {
            return /^\d+\.\s+/.test(row.label || '');
        },
        getScheduleDateRow(block) {
            return (block.rows || []).find((row) => row.label === 'Schedule Date') || null;
        },
        getScheduleDateValues(block) {
            const scheduleRow = this.getScheduleDateRow(block);
            const values = scheduleRow && Array.isArray(scheduleRow.values)
                ? scheduleRow.values.slice(0, PDF_DATA_COLS)
                : [];
            while (values.length < PDF_DATA_COLS) values.push('');
            return values;
        },
        isActiveDataColumn(block, colIndex) {
            const scheduleValues = this.getScheduleDateValues(block);
            return String(scheduleValues[colIndex] || '').trim() !== '';
        },
        normalizeRowValues(row) {
            const values = Array.isArray(row.values) ? row.values.slice(0, PDF_DATA_COLS) : [];
            while (values.length < PDF_DATA_COLS) values.push('');
            return values;
        },
        getPublicAssetUrl(path) {
            const baseUrl = process.env.BASE_URL || '/';
            return `${baseUrl}${path.replace(/^\//, '')}`;
        },
        arrayBufferToBase64(buffer) {
            let binary = '';
            const bytes = new Uint8Array(buffer);
            for (let i = 0; i < bytes.byteLength; i += 1) {
                binary += String.fromCharCode(bytes[i]);
            }
            return btoa(binary);
        },
        async registerPdfFonts(pdf) {
            try {
                if (!pdfFontCache.loaded) {
                    const [normalRes, boldRes] = await Promise.all([
                        fetch(this.getPublicAssetUrl(PDF_FONT_FILES.normal)),
                        fetch(this.getPublicAssetUrl(PDF_FONT_FILES.bold)),
                    ]);
                    if (!normalRes.ok || !boldRes.ok) {
                        throw new Error('Calibri font files not found');
                    }
                    pdfFontCache.normal = this.arrayBufferToBase64(await normalRes.arrayBuffer());
                    pdfFontCache.bold = this.arrayBufferToBase64(await boldRes.arrayBuffer());
                    pdfFontCache.loaded = true;
                }

                pdf.addFileToVFS('Calibri-normal.ttf', pdfFontCache.normal);
                pdf.addFont('Calibri-normal.ttf', PDF_FONT_FAMILY, 'normal');
                pdf.addFileToVFS('Calibri-bold.ttf', pdfFontCache.bold);
                pdf.addFont('Calibri-bold.ttf', PDF_FONT_FAMILY, 'bold');
                this.pdfFontFamily = PDF_FONT_FAMILY;
            } catch (err) {
                console.warn('Failed to load Calibri fonts, falling back to Helvetica', err);
                this.pdfFontFamily = 'helvetica';
            }
        },
        setPdfFont(pdf, fontStyle = 'normal') {
            pdf.setFont(this.pdfFontFamily, fontStyle === 'bold' ? 'bold' : 'normal');
        },
        loadImage(url) {
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = () => resolve(null);
                img.src = url;
            });
        },
        loadLogoImage() {
            return this.loadImage(this.getPublicAssetUrl(PDF_LOGO_PATH));
        },
        loadCheckIconImage() {
            return this.loadImage(this.getPublicAssetUrl(PDF_CHECK_ICON_PATH));
        },
        getImageDataUrl(img) {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext('2d').drawImage(img, 0, 0);
            return canvas.toDataURL('image/png');
        },
        async loadCheckIconForPdf() {
            const img = await this.loadCheckIconImage();
            if (!img) return null;
            return {
                img,
                dataUrl: this.getImageDataUrl(img),
            };
        },
        getColumnWidths(tableWidth) {
            const labelWidth = tableWidth * PDF_LABEL_COL_RATIO;
            const dataWidth = (tableWidth - labelWidth) / PDF_DATA_COLS;
            return [labelWidth, ...Array(PDF_DATA_COLS).fill(dataWidth)];
        },
        isInactivePdfDataColumn(block, colIndex) {
            return colIndex > 0 && !this.isActiveDataColumn(block, colIndex - 1);
        },
        getPdfCellStyle(row, rowIndex, colIndex, block) {
            if (block && this.isInactivePdfDataColumn(block, colIndex)) {
                return { fontStyle: 'normal', fillColor: [255, 255, 255], hideBorder: true };
            }
            if (this.isSectionRow(row)) {
                return { fontStyle: 'bold', fillColor: [240, 240, 240] };
            }
            if (rowIndex === 0 && colIndex > 0) {
                return { fontStyle: 'bold', fillColor: [232, 232, 232] };
            }
            if (rowIndex > 0 && rowIndex < 7 && colIndex === 0) {
                return { fontStyle: 'normal', fillColor: [255, 255, 255] };
            }
            if (rowIndex > 0 && rowIndex < 7 && colIndex > 0) {
                return { fontStyle: 'normal', fillColor: [232, 232, 232] };
            }
            if (row.label === 'Block' && colIndex > 0) {
                return { fontStyle: 'bold' };
            }
            if (colIndex === 0) {
                return { fontStyle: 'bold', fillColor: [245, 245, 245] };
            }
            return { fontStyle: 'normal' };
        },
        getLineHeight(fontSize) {
            return fontSize * 0.38;
        },
        isCheckmarkValue(text) {
            const value = String(text || '').trim();
            return ['✓', '√', '\u2713', '\u221a', '/'].includes(value);
        },
        isCheckmarkLines(lines) {
            return lines.length === 1 && this.isCheckmarkValue(lines[0]);
        },
        drawPdfCheckIcon(pdf, checkIcon, x, y, width, height) {
            if (!checkIcon || !checkIcon.dataUrl) return;

            const iconHeight = PDF_CHECK_ICON_SIZE_MM;
            const iconWidth = (checkIcon.img.width / checkIcon.img.height) * iconHeight;
            const iconX = x + (width - iconWidth) / 2;
            const iconY = y + (height - iconHeight) / 2;

            pdf.addImage(checkIcon.dataUrl, 'PNG', iconX, iconY, iconWidth, iconHeight);
        },
        wrapCellText(pdf, text, maxWidth, fontStyle, fontSize) {
            if (this.isCheckmarkValue(text)) {
                return [String(text).trim()];
            }
            this.setPdfFont(pdf, fontStyle || 'normal');
            pdf.setFontSize(fontSize);
            return pdf.splitTextToSize(String(text || ''), Math.max(maxWidth, 1));
        },
        calculateTableLayout(pdf, rows, colWidths, fontSize, block) {
            const lineHeight = this.getLineHeight(fontSize);
            const cellPadding = 0.5;

            const layoutRows = rows.map((row, rowIndex) => {
                const cells = [row.label, ...this.normalizeRowValues(row)];
                let maxLines = 1;
                const cellLines = cells.map((text, colIndex) => {
                    const style = this.getPdfCellStyle(row, rowIndex, colIndex, block);
                    const maxWidth = colWidths[colIndex] - cellPadding * 2 - 0.5;
                    const lines = this.wrapCellText(pdf, text, maxWidth, style.fontStyle, fontSize);
                    maxLines = Math.max(maxLines, lines.length);
                    return lines;
                });
                const height = maxLines * lineHeight + cellPadding * 2;
                return { cellLines, height, row, rowIndex };
            });

            const totalHeight = layoutRows.reduce((sum, row) => sum + row.height, 0);
            return { rows: layoutRows, totalHeight };
        },
        drawCellText(pdf, lines, x, y, width, fontSize, align) {
            const lineHeight = this.getLineHeight(fontSize);
            lines.forEach((line, index) => {
                const textY = y + index * lineHeight;
                if (align === 'center') {
                    pdf.text(line, x + width / 2, textY, { align: 'center', maxWidth: width });
                } else {
                    pdf.text(line, x, textY, { align: 'left', maxWidth: width });
                }
            });
        },
        drawBlockHeader(pdf, block, pageWidth, logoImg) {
            const startY = PDF_MARGIN_MM;
            const logoHeight = PDF_LOGO_HEIGHT_MM;
            const textAreaWidth = pageWidth - PDF_MARGIN_MM * 2;
            const textCenterX = pageWidth / 2;
            let y = startY;

            if (logoImg) {
                const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
                const logoCanvas = document.createElement('canvas');
                logoCanvas.width = logoImg.width;
                logoCanvas.height = logoImg.height;
                logoCanvas.getContext('2d').drawImage(logoImg, 0, 0);
                pdf.addImage(
                    logoCanvas.toDataURL('image/jpeg'),
                    'JPEG',
                    PDF_MARGIN_MM,
                    y,
                    logoWidth,
                    logoHeight
                );
                y += logoHeight + 1.5;
            }

            if (block.title) {
                this.setPdfFont(pdf, 'bold');
                pdf.setFontSize(8);
                pdf.setTextColor(0, 0, 0);
                const titleLines = pdf.splitTextToSize(block.title, textAreaWidth);
                titleLines.forEach((line, index) => {
                    pdf.text(line, textCenterX, y + index * PDF_HEADER_TITLE_LINE_HEIGHT, {
                        align: 'center',
                        maxWidth: textAreaWidth,
                    });
                });
                y += titleLines.length * PDF_HEADER_TITLE_LINE_HEIGHT + PDF_HEADER_TITLE_PERIOD_GAP;
            }

            if (block.period) {
                this.setPdfFont(pdf, 'bold');
                pdf.setFontSize(7);
                pdf.text(block.period, textCenterX, y, {
                    align: 'center',
                    maxWidth: textAreaWidth,
                });
                y += PDF_HEADER_PERIOD_LINE_HEIGHT;
            }

            return y + 0.5;
        },
        drawPdfFooter(pdf, pageWidth, pageHeight, pageNum, exportMeta) {
            const { exportedBy, exportDate } = exportMeta;
            const lineGap = 3.5;
            const firstLineY = pageHeight - 8;
            const secondLineY = firstLineY + lineGap;

            this.setPdfFont(pdf, 'normal');
            pdf.setFontSize(7);
            pdf.setTextColor(0, 0, 0);
            pdf.text(`Export Date: ${exportDate}`, PDF_MARGIN_MM, firstLineY);
            pdf.text(`Exported By: ${exportedBy}`, PDF_MARGIN_MM, secondLineY);
            pdf.text(`Page ${pageNum}`, pageWidth - PDF_MARGIN_MM, secondLineY, { align: 'right' });
        },
        getScaledRowHeights(layoutRows, areaHeight) {
            if (!layoutRows.length) return [];

            const naturalTotal = layoutRows.reduce((sum, row) => sum + row.height, 0);
            if (naturalTotal <= 0) return layoutRows.map(() => areaHeight / layoutRows.length);

            const scale = areaHeight / naturalTotal;
            const rowHeights = layoutRows.map((row) => row.height * scale);
            const heightDiff = areaHeight - rowHeights.reduce((sum, height) => sum + height, 0);
            rowHeights[rowHeights.length - 1] += heightDiff;

            return rowHeights;
        },
        getLastBorderedColumnIndex(block) {
            let lastIndex = 0;
            const totalCols = 1 + PDF_DATA_COLS;
            for (let colIndex = 1; colIndex < totalCols; colIndex += 1) {
                if (!this.isInactivePdfDataColumn(block, colIndex)) {
                    lastIndex = colIndex;
                }
            }
            return lastIndex;
        },
        getColumnRightX(margin, colWidths, colIndex) {
            return margin + colWidths.slice(0, colIndex + 1).reduce((sum, width) => sum + width, 0);
        },
        drawBlockTable(pdf, block, area, checkIcon) {
            const rows = block.rows || [];
            if (!rows.length) return;

            const colWidths = this.getColumnWidths(area.width);
            let fontSize = PDF_DEFAULT_FONT_SIZE;
            let layout = this.calculateTableLayout(pdf, rows, colWidths, fontSize, block);

            while (layout.totalHeight > area.height && fontSize > PDF_MIN_FONT_SIZE) {
                fontSize -= 0.25;
                layout = this.calculateTableLayout(pdf, rows, colWidths, fontSize, block);
            }

            const rowHeights = this.getScaledRowHeights(layout.rows, area.height);
            pdf.setFontSize(fontSize);
            const cellPadding = 0.5;
            let y = area.startY;

            layout.rows.forEach((rowLayout, rowIndex) => {
                const { row, rowIndex: dataRowIndex, cellLines } = rowLayout;
                const rowHeight = rowHeights[rowIndex];

                cellLines.forEach((lines, colIndex) => {
                    const x = area.margin + colWidths.slice(0, colIndex).reduce((sum, width) => sum + width, 0);
                    const width = colWidths[colIndex];
                    const style = this.getPdfCellStyle(row, dataRowIndex, colIndex, block);

                    if (style.fillColor) {
                        pdf.setFillColor(style.fillColor[0], style.fillColor[1], style.fillColor[2]);
                        pdf.rect(x, y, width, rowHeight, 'F');
                    }

                    if (!style.hideBorder) {
                        pdf.setTextColor(0, 0, 0);
                        const contentX = x + cellPadding;
                        const contentY = y + cellPadding;
                        const contentWidth = width - cellPadding * 2;
                        const contentHeight = rowHeight - cellPadding * 2;

                        if (this.isCheckmarkLines(lines)) {
                            this.drawPdfCheckIcon(pdf, checkIcon, contentX, contentY, contentWidth, contentHeight);
                        } else {
                            this.setPdfFont(pdf, style.fontStyle || 'normal');
                            this.drawCellText(
                                pdf,
                                lines,
                                contentX,
                                contentY + fontSize * 0.3,
                                contentWidth,
                                fontSize,
                                colIndex === 0 ? 'left' : 'center'
                            );
                        }
                    }
                });

                y += rowHeight;
            });

            // Draw borders after fills so inactive columns do not paint over adjacent edges.
            pdf.setDrawColor(51, 51, 51);
            pdf.setLineWidth(0.1);
            let borderY = area.startY;
            layout.rows.forEach((rowLayout, rowIndex) => {
                const { row, rowIndex: dataRowIndex, cellLines } = rowLayout;
                const rowHeight = rowHeights[rowIndex];

                cellLines.forEach((lines, colIndex) => {
                    const style = this.getPdfCellStyle(row, dataRowIndex, colIndex, block);
                    if (style.hideBorder) return;

                    const x = area.margin + colWidths.slice(0, colIndex).reduce((sum, width) => sum + width, 0);
                    const width = colWidths[colIndex];
                    pdf.rect(x, borderY, width, rowHeight, 'S');
                });

                borderY += rowHeight;
            });

            const lastBorderedCol = this.getLastBorderedColumnIndex(block);
            const tableRightX = this.getColumnRightX(area.margin, colWidths, lastBorderedCol);
            pdf.line(tableRightX, area.startY, tableRightX, y);
        },
        async buildPdfFromBlocks(blocks, logoImg, checkIcon, filename) {
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            await this.registerPdfFonts(pdf);
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const tableWidth = pageWidth - PDF_MARGIN_MM * 2 - PDF_TABLE_RIGHT_EDGE_PAD_MM;
            const exportMeta = this.getPdfExportMeta();

            blocks.forEach((block, index) => {
                if (index > 0) pdf.addPage();

                const headerBottom = this.drawBlockHeader(pdf, block, pageWidth, logoImg);
                this.drawPdfFooter(pdf, pageWidth, pageHeight, index + 1, exportMeta);
                const tableStartY = headerBottom + 0.5;
                const tableEndY = pageHeight - PDF_FOOTER_MM - 1;
                this.drawBlockTable(pdf, block, {
                    margin: PDF_MARGIN_MM,
                    width: tableWidth,
                    startY: tableStartY,
                    height: tableEndY - tableStartY,
                }, checkIcon);
            });

            pdf.save(filename);
        },
        async exportPdf(filename) {
            if (!this.blocks.length) {
                throw new Error('No report blocks found');
            }

            const [logoImg, checkIcon] = await Promise.all([
                this.loadLogoImage(),
                this.loadCheckIconForPdf(),
            ]);
            const defaultName = `Checklist-Report-${moment().format('DD-MMM-YYYY')}.pdf`;
            await this.buildPdfFromBlocks(this.blocks, logoImg, checkIcon, filename || defaultName);
        },
    },
};
</script>

<style scoped>
.job-gofm-report-grid {
    padding: 8px 12px 20px;
}

.gofm-report-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    table-layout: fixed;
}

.gofm-report-table th,
.gofm-report-table td {
    border: 1px solid #333;
    padding: 4px 6px;
    vertical-align: top;
    word-break: break-word;
}

.gofm-report-table tbody tr:nth-child(-n+7) .gofm-report-label {
    background: #fff;
}

.gofm-report-table tbody tr:nth-child(n+2):nth-child(-n+7) .gofm-report-label,
.gofm-report-table tbody tr:nth-child(7) .gofm-report-value {
    font-weight: normal;
}

.gofm-report-table tbody tr:nth-child(-n+7) .gofm-report-value {
    background: #e8e8e8;
}

.gofm-report-label {
    width: 35%;
    max-width: 35%;
    font-weight: 600;
    background: #f5f5f5;
    text-align: left;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.gofm-report-value {
    text-align: center;
    min-width: 110px;
}

.gofm-report-empty-col {
    border: none !important;
    background: #fff !important;
}

.gofm-report-header-row .gofm-report-value {
    background: #e8e8e8;
    font-weight: 700;
}

.gofm-report-section-row .gofm-report-label {
    background: #f0f0f0;
    font-weight: 700;
}

.gofm-report-block-row .gofm-report-value {
    font-weight: 600;
}
</style>
